import { USER_PREFERENCES_QUERY_KEY, useUserPreferences } from '@/hooks/use-user-preferences';
import { putUserPreferences } from '@/lib/api';
import { isLearnableKeyterm, mergePersonalKeyterms } from '@/lib/stt/personal-keyterms';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
const SYNC_DEBOUNCE_MS = 800;
/**
 * Per-radiologist dictation vocabulary stored in user_preferences.reportSettings.
 */
// Stable empty default so a missing list does not re-create persistTerms per render.
const NO_TERMS = [];
export function useDictationKeyterms() {
    const { preferences } = useUserPreferences();
    const queryClient = useQueryClient();
    const syncTimer = useRef(null);
    const terms = preferences.reportSettings.dictationKeyterms ?? NO_TERMS;
    const persistTerms = useCallback((nextTerms) => {
        const nextReportSettings = {
            ...preferences.reportSettings,
            dictationKeyterms: nextTerms,
        };
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, {
            ...preferences,
            reportSettings: nextReportSettings,
        });
        if (syncTimer.current)
            clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
            putUserPreferences({
                timezone: preferences.timezone,
                timeFormat: preferences.timeFormat,
                locale: preferences.locale,
                sidebarClinicDisplayName: preferences.sidebarClinicDisplayName,
                reportSettings: nextReportSettings,
            })
                .then(() => queryClient.invalidateQueries({ queryKey: USER_PREFERENCES_QUERY_KEY }))
                .catch(() => {
                /* keep optimistic state */
            });
        }, SYNC_DEBOUNCE_MS);
    }, [preferences, queryClient]);
    const appendTerm = useCallback((raw) => {
        if (!isLearnableKeyterm(raw))
            return;
        const merged = mergePersonalKeyterms(terms, [raw]);
        if (merged.length === terms.length && merged.every((term, index) => term === terms[index])) {
            return;
        }
        persistTerms(merged);
    }, [persistTerms, terms]);
    const replaceTerms = useCallback((nextTerms) => {
        persistTerms(mergePersonalKeyterms([], nextTerms));
    }, [persistTerms]);
    return { terms, appendTerm, replaceTerms };
}
